// File: src/pages/ProductViewPage.tsx

import React, { useEffect, useState, useRef, MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../Header/CartContext';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart, Zap, ChevronRight } from 'lucide-react';

interface Product {
    id: number;
    productName: string;
    description: string;
    price: number;
    discount?: number;
    offer?: string;
    tax?: number;
    imageUrl?: string;
    otherImageUrls?: string[];
    stockQuantity: number;
    typeQuantity: number;
    status: string;
    measurementUnit: string;
    categoryName?: string;
    categoryTypeName?: string;
    categoryId?: number;
}

const API_BASE_URL = 'https://ayushvatika.com';
const IMAGE_BASE_URL = 'https://ayushvatika.com/images/products/';

const ProductViewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    
    // States
    const [product, setProduct] = useState<Product | null>(null);
    const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
    // State to hold all splitted images
    const [allImages, setAllImages] = useState<string[]>([]);
    
    const imageRef = useRef<HTMLDivElement>(null);

    // --- Fetch Data ---
    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchProductDetails = async () => {
            setIsLoading(true);
            try {
                const productResponse = await fetch(`${API_BASE_URL}/api/products/${id}`);
                if (!productResponse.ok) throw new Error(`HTTP error! status: ${productResponse.status}`);
                const productData: Product = await productResponse.json();
                setProduct(productData);
                
                // YAHAN FIX KIYA HAI: Split comma-separated image string
                let dbImages: string[] = [];
                if (productData.imageUrl && productData.imageUrl.trim() !== '') {
                    dbImages = productData.imageUrl.split(',').map(img => img.trim()).filter(Boolean);
                }
                const combinedImages = [...dbImages, ...(productData.otherImageUrls || [])];
                
                setAllImages(combinedImages);
                setSelectedImage(combinedImages.length > 0 ? combinedImages[0] : undefined);

                if (productData.categoryId) {
                    try {
                        const relatedResponse = await fetch(`${API_BASE_URL}/api/products/category/${productData.categoryId}`);
                        if (relatedResponse.ok) {
                            const relatedData: Product[] = await relatedResponse.json();
                            setRelatedProducts(relatedData.filter(p => p.id !== productData.id));
                        }
                    } catch (err) { console.log("Rel err", err); }
                } else {
                     const allResponse = await fetch(`${API_BASE_URL}/api/products`);
                     if(allResponse.ok) {
                         const allData: Product[] = await allResponse.json();
                         setRelatedProducts(allData.filter(p => p.id !== productData.id).slice(0, 5));
                     }
                }
            } catch (e: any) {
                setError(e.message);
            } finally {
                setIsLoading(false);
            }
        };
        if (id) fetchProductDetails();
    }, [id]);

    // --- Handlers ---
    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (!imageRef.current) return;
        if (window.innerWidth < 1024) return;

        const { left, top, width, height } = imageRef.current.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;
        (imageRef.current.querySelector('img') as HTMLImageElement).style.transformOrigin = `${x}% ${y}%`;
        (imageRef.current.querySelector('img') as HTMLImageElement).style.transform = 'scale(2)';
    };

    const handleMouseLeave = () => {
        if (imageRef.current) {
            (imageRef.current.querySelector('img') as HTMLImageElement).style.transformOrigin = 'center center';
            (imageRef.current.querySelector('img') as HTMLImageElement).style.transform = 'scale(1)';
        }
    };

    const calculateFinalPrice = (price: number, discount?: number) => (discount ? price - (price * (discount / 100)) : price);
    const isOutOfStock = product?.stockQuantity === 0;

    const handleBuyNow = () => {
        if (!product || isOutOfStock) {
            if(isOutOfStock) toast.error("Out of stock");
            return;
        }
        const buyNowProduct = {
            id: product.id,
            productId: product.id,
            productName: product.productName,
            price: calculateFinalPrice(product.price, product.discount),
            imageUrl: allImages.length > 0 ? `${IMAGE_BASE_URL}${encodeURIComponent(allImages[0])}` : '',
            seller: 'Ayush Vatika',
            quantity: 1
        };
        localStorage.setItem('buyNowProduct', JSON.stringify(buyNowProduct));
        localStorage.setItem('checkoutMode', 'buyNow');
        navigate("/checkoutpage");
    };

    const handleAddToCart = () => {
        if (!product || isOutOfStock) {
            if(isOutOfStock) toast.error("Out of stock");
            return;
        }
        addToCart({
            productId: product.id,
            productName: product.productName,
            price: calculateFinalPrice(product.price, product.discount),
            imageUrl: allImages.length > 0 ? `${IMAGE_BASE_URL}${encodeURIComponent(allImages[0])}` : '',
            seller: 'Ayush Vatika',
        }, 1);
        toast.success(`${product.productName} added to cart!`);
    };

    const handleRelatedAddToCart = (e: React.MouseEvent, relProduct: Product) => {
        e.stopPropagation();
        let relFirstImg = '';
        if (relProduct.imageUrl) {
            relFirstImg = relProduct.imageUrl.split(',')[0].trim();
        }

        addToCart({
            productId: relProduct.id,
            productName: relProduct.productName,
            price: calculateFinalPrice(relProduct.price, relProduct.discount),
            imageUrl: relFirstImg ? `${IMAGE_BASE_URL}${encodeURIComponent(relFirstImg)}` : '',
            seller: 'Ayush Vatika',
        }, 1);
        toast.success("Added to cart");
    };

    if (isLoading) return <div className="min-h-screen flex justify-center items-center text-green-700 font-medium">Loading details...</div>;
    if (error || !product) return <div className="min-h-screen flex justify-center items-center text-red-500">Error loading product.</div>;

    const finalPrice = calculateFinalPrice(product.price, product.discount);
    
    const specs = [
        { label: "Brand", value: "Ayush Vatika" },
        { label: "Model Name", value: product.productName },
        { label: "Quantity", value: `${product.typeQuantity} ${product.measurementUnit}` },
        { label: "Category", value: product.categoryName || "Wellness" },
        { label: "Type", value: product.categoryTypeName || "Supplement" },
        { label: "Stock Status", value: product.stockQuantity > 0 ? "In Stock" : "Out of Stock" },
        { label: "Ideal For", value: "Men & Women" },
        { label: "Organic", value: "Yes" },
    ];

    return (
        <div className="bg-white min-h-screen pb-12 font-sans relative">
            <Helmet>
                <title>{product.productName} - Buy Online | Ayush Vatika</title>
            </Helmet>

            <div className="container mx-auto px-4 py-4 max-w-7xl">
                
                <div className="flex flex-col lg:flex-row gap-6">
                    
                    {/* --- LEFT COLUMN: Images & Buttons --- */}
                    <div className="lg:w-[40%] flex-shrink-0 relative lg:sticky lg:top-4 h-fit mb-6 lg:mb-0">
                        
                        {/* Wrapper: Mobile = Column Reverse, Desktop = Row */}
                        <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-2">
                            
                            {/* Thumbnails */}
                            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto no-scrollbar py-2 lg:py-0 px-1 lg:px-0 w-full lg:w-20 lg:h-[450px]">
                                {allImages.map((imgUrl, index) => (
                                    <div 
                                        key={index}
                                        className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 border rounded p-1 cursor-pointer transition-all bg-white
                                        ${selectedImage === imgUrl ? 'border-green-600 ring-1 ring-green-600' : 'border-gray-300 hover:border-green-600'}`}
                                        onMouseEnter={() => setSelectedImage(imgUrl)}
                                        onClick={() => setSelectedImage(imgUrl)}
                                    >
                                        <img src={`${IMAGE_BASE_URL}${encodeURIComponent(imgUrl)}`} alt="thumb" className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>

                            {/* Main Image View */}
                            <div 
                                className="flex-1 relative border border-gray-200 rounded-sm h-[350px] md:h-[450px] flex items-center justify-center bg-white cursor-crosshair overflow-hidden"
                                onMouseMove={handleMouseMove}
                                onMouseLeave={handleMouseLeave}
                                ref={imageRef}
                            >
                                <img 
                                    src={selectedImage ? `${IMAGE_BASE_URL}${encodeURIComponent(selectedImage)}` : ''} 
                                    alt={product.productName} 
                                    className="max-h-full max-w-full object-contain p-4 transition-transform duration-150"
                                />
                                {product.discount && (
                                    <span className="absolute top-4 right-4 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded shadow-sm">
                                        {product.discount}% off
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* --- Action Buttons --- */}
                        <div className="flex gap-3 mt-4 w-full">
                            <button 
                                onClick={handleAddToCart}
                                disabled={isOutOfStock}
                                className={`flex-1 py-3 md:py-4 rounded-sm font-bold text-sm md:text-lg text-white flex items-center justify-center gap-2 shadow-sm uppercase ${isOutOfStock ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#7c3aed] hover:bg-[#6d28d9]'}`}
                            >
                                <ShoppingCart size={20} className="md:w-6 md:h-6" fill="white" />
                                {isOutOfStock ? "NO STOCK" : "ADD TO CART"}
                            </button>
                            <button 
                                onClick={handleBuyNow}
                                disabled={isOutOfStock}
                                className={`flex-1 py-3 md:py-4 rounded-sm font-bold text-sm md:text-lg text-white flex items-center justify-center gap-2 shadow-sm uppercase ${isOutOfStock ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#16a34a] hover:bg-[#15803d]'}`}
                            >
                                <Zap size={20} className="md:w-6 md:h-6" fill="white" />
                                {isOutOfStock ? "NO STOCK" : "BUY NOW"}
                            </button>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: Details & Specifications --- */}
                    <div className="lg:w-[60%] pl-0 lg:pl-6">
                        <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                            <span>Home</span> <ChevronRight size={12}/> 
                            <span>{product.categoryName || 'Product'}</span> <ChevronRight size={12}/> 
                            <span className="truncate">{product.productName}</span>
                        </div>

                        <h1 className="text-xl md:text-2xl font-medium text-gray-800 mb-2">{product.productName}</h1>
                        
                        <div className="flex items-center gap-2 mb-3">
                            <span className="bg-green-700 text-white text-sm font-bold px-2 py-0.5 rounded flex items-center gap-1">
                                4.4 ★
                            </span>
                            <span className="text-gray-500 font-medium text-sm">2,841 Ratings & 305 Reviews</span>
                        </div>

                        <div className="mb-4">
                            <div className="flex items-baseline gap-3">
                                <span className="text-3xl font-bold text-black">₹{finalPrice.toFixed(0)}</span>
                                {product.discount && (
                                    <>
                                        <span className="text-gray-500 line-through text-lg">₹{product.price.toFixed(0)}</span>
                                        <span className="text-green-700 font-bold text-lg">{product.discount}% off</span>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">+ ₹40 Secured Packaging Fee</p>
                        </div>

                        {/* --- FULL DESCRIPTION --- */}
                        <div className="border border-gray-200 rounded-sm p-4 mb-8">
                            <h2 className="text-lg font-bold text-gray-800 mb-3">Product Description</h2>
                            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                                {product.description}
                                {"\n\n"}
                                Experience the natural benefits of {product.productName}.
                                {"\n\n"}
                                Storage Instructions: Store in a cool, dry place.
                            </p>
                        </div>

                        {/* --- SPECIFICATIONS TABLE --- */}
                        <div className="border border-gray-200 rounded-sm mb-6">
                            <div className="p-4 border-b border-gray-200 bg-white">
                                <h2 className="text-lg font-bold text-gray-800">Specifications</h2>
                            </div>
                            <div className="p-4">
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase">General</h3>
                                    <div className="space-y-3">
                                        {specs.map((item, idx) => (
                                            <div key={idx} className="flex flex-col sm:flex-row text-sm">
                                                <div className="sm:w-1/3 text-gray-500">{item.label}</div>
                                                <div className="sm:w-2/3 text-gray-900 font-medium">{item.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

               {/* --- TRENDING SIMILAR PRODUCTS --- */}
                {relatedProducts.length > 0 && (
                    <div className="mt-20">
                        <div className="flex justify-between items-end mb-8 border-b border-gray-100 pb-4">
                            <h2 className="text-2xl font-black text-[#2e3d2f] uppercase tracking-tight">
                                Trending <span className="font-sans text-green-700 ">Similar Products</span>
                            </h2>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                            {relatedProducts.map(rel => (
                                <div key={rel.id} onClick={() => navigate(`/product/${rel.id}`)} className="group cursor-pointer bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all">
                                    <div className="aspect-square bg-[#f8faf7] relative overflow-hidden">
                                        {rel.discount && (
                                            <div className="absolute top-2 left-2 bg-red-600 text-white text-[8px] font-black px-2 py-0.5 rounded tracking-tighter uppercase z-10">
                                                {rel.discount}% OFF
                                            </div>
                                        )}
                                        <img src={rel.imageUrl ? `${IMAGE_BASE_URL}${rel.imageUrl.split(',')[0]}` : ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="rel" />
                                    </div>
                                    <div className="p-4 text-center">
                                        <h3 className="text-[11px] font-bold text-[#2e3d2f] line-clamp-1 group-hover:text-green-700 uppercase mb-2">{rel.productName}</h3>
                                        <div className="flex items-center justify-center gap-2">
                                            <span className="text-[10px] text-gray-400 line-through font-bold">₹{rel.price}</span>
                                            <span className="text-sm font-black text-green-700">₹{calculateFinalPrice(rel.price, rel.discount).toFixed(0)}</span>
                                        </div>
                                        <button className="w-full mt-4 py-2 bg-[#24422b] text-white text-[9px] font-bold rounded-lg uppercase tracking-widest group-hover:bg-green-600 transition-colors shadow-md">Add to Bag</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* MOBILE ACTION BAR */}
            {/* <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:hidden z-50 flex gap-3 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
                <button onClick={handleAddToCart} disabled={isOutOfStock} className="flex-1 py-3 bg-[#24422b] text-white rounded-xl font-bold text-[11px] uppercase tracking-[2px] shadow-lg">
                    Add to Bag
                </button>
                <button onClick={handleBuyNow} disabled={isOutOfStock} className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold text-[11px] uppercase tracking-[2px] shadow-lg">
                    Buy Now
                </button>
            </div> */}
        </div>
    );
};

export default ProductViewPage;